/**
 * Les paramètres d'une affaire : tout ce qui la gère plutôt que la travaille.
 *
 * **Rangés par conséquence croissante**, et c'est le seul ordre défendable :
 * on annule des traitements avant de purger des révisions, on purge avant
 * d'archiver, on archive avant de détruire. Un écran qui mêlerait « Actualiser »
 * et « Supprimer définitivement » finirait par faire cliquer l'un pour l'autre.
 *
 * **Chaque section dit sa conséquence avant son bouton.** « Archiver » ne dit
 * rien ; « l'affaire se consulte, elle ne se modifie plus » dit ce qui change.
 * C'est ce qui distingue une décision d'un réflexe.
 *
 * Ce qui ne s'y trouve pas, et pourquoi : les conventions de métré, les membres
 * et le cadre de sortie que la maquette dessine ici. Aucun point d'accès ne les
 * porte — les afficher promettrait ce que la plateforme ne tient pas.
 */
import { useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { Avis } from "../../composants/avis";
import { Chargement } from "../../composants/etats";
import { poids, quand } from "../../composants/formats";
import { useDonnees } from "../../composants/donnees";
import { Supprimer } from "./Supprimer";

interface RevisionEchue {
  revision_id: string;
  nom: string;
  octets: number;
  echue_le: string;
}

export function Parametres({
  affaireId,
  etat,
  rafraichir,
}: {
  affaireId: string;
  etat: string;
  rafraichir: () => void;
}) {
  const [avis, poserAvis] = useState("");
  const [erreur, poserErreur] = useState("");
  const [occupe, poserOccupe] = useState("");
  const [suppression, poserSuppression] = useState(false);

  const echues = useDonnees<{ donnees: RevisionEchue[] }>(
    () => api.lire(`/api/v1/affaires/${affaireId}/revisions-echues`),
    [affaireId],
  );

  /** Un geste, son message, et le rafraîchissement qui suit.
   *
   * Factorisé parce que les quatre se ressemblent — et qu'une erreur laissée
   * au hasard de quatre copies finirait par en oublier une. */
  async function agir(
    quoi: string,
    chemin: string,
    dire: (issue: Record<string, unknown>) => string,
  ) {
    poserErreur("");
    poserAvis("");
    poserOccupe(quoi);
    try {
      const issue = await api.ecrire<{ donnees: Record<string, unknown> }>(chemin);
      poserAvis(dire(issue.donnees));
      echues.recharger();
      rafraichir();
    } catch (exc) {
      poserErreur(
        exc instanceof ErreurApi ? exc.message : "Le geste n'a pas abouti.",
      );
    } finally {
      poserOccupe("");
    }
  }

  const aPurger = echues.donnees?.donnees ?? [];

  return (
    <div className="synthese">
      {erreur && <Avis ton="erreur" cle={erreur}>{erreur}</Avis>}
      {avis && <Avis ton="info" cle={avis}>{avis}</Avis>}

      {/* 1 — Ce qui tourne. Le geste le plus anodin : rien n'est détruit, et
             ce qui est déjà pris continue. */}
      <section className="reglage">
        <div className="reglage__propos">
          <h3>Traitements en attente</h3>
          <p>
            Renonce à ce qui n'a pas encore commencé. Ce qu'un collecteur a
            déjà pris n'est pas interrompu : il écrit peut-être en ce moment,
            et lui couper l'herbe sous le pied laisserait un fichier à demi
            écrit.
          </p>
        </div>
        <button
          type="button"
          className="bouton"
          disabled={occupe !== ""}
          onClick={() =>
            void agir("travaux",
              `/api/v1/affaires/${affaireId}/travaux/annulation`,
              (i) =>
                `${i.annules} traitement(s) annulé(s)` +
                (Number(i.encore_en_cours)
                  ? `, ${i.encore_en_cours} encore en cours.`
                  : "."))
          }
        >
          {occupe === "travaux" ? "Annulation…" : "Annuler ce qui attend"}
        </button>
      </section>

      {/* 2 — Les octets échus. Destructif, mais borné à ce que la rétention
             autorise déjà, et jamais à ce qui compose un instantané. */}
      <section className="reglage">
        <div className="reglage__propos">
          <h3>Révisions échues</h3>
          <p>
            Efface les révisions dont la durée de conservation promise est
            passée. Une révision qui compose un instantané n'est{" "}
            <strong>jamais</strong> effacée, échéance ou non : un métré remis la
            cite, et le chiffrage cesserait d'être vérifiable.
          </p>
          {echues.charge && !echues.donnees ? (
            <Chargement lignes={1} />
          ) : aPurger.length === 0 ? (
            <p className="discret">Rien n'est échu : il n'y a rien à effacer.</p>
          ) : (
            <ul className="reglage__liste">
              {aPurger.slice(0, 5).map((revision) => (
                <li key={revision.revision_id}>
                  <span className="mono">{revision.nom}</span>{" "}
                  <span className="discret">
                    · {poids(revision.octets)} · échue le{" "}
                    {quand(revision.echue_le)}
                  </span>
                </li>
              ))}
              {aPurger.length > 5 && (
                <li className="discret">et {aPurger.length - 5} autre(s).</li>
              )}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="bouton bouton--danger"
          disabled={occupe !== "" || aPurger.length === 0}
          onClick={() =>
            void agir("purge",
              `/api/v1/affaires/${affaireId}/revisions-echues/purge`,
              (i) => `${i.effacees} révision(s) effacée(s), ${poids(Number(i.octets))} libérés.`)
          }
        >
          {occupe === "purge"
            ? "Effacement…"
            : `Effacer ${aPurger.length || ""} révision(s) échue(s)`.trim()}
        </button>
      </section>

      {/* 3 — Le cycle de vie. Réversible dans les deux sens, et c'est ce qui
             le rend employable sans hésiter. */}
      <section className="reglage">
        <div className="reglage__propos">
          <h3>Cycle de vie</h3>
          <p>
            {etat === "ARCHIVED"
              ? "Cette affaire est archivée : elle se consulte, elle ne se "
                + "modifie plus. La rouvrir la rend de nouveau prête à recevoir "
                + "des sources."
              : "Archiver met l'affaire de côté sans rien détruire : elle se "
                + "consulte, elle ne se modifie plus. Le geste se défait."}
          </p>
        </div>
        {etat === "ARCHIVED" ? (
          <button
            type="button"
            className="bouton"
            disabled={occupe !== ""}
            onClick={() =>
              void agir("cycle", `/api/v1/affaires/${affaireId}/reouverture`,
                () => "L'affaire est rouverte.")
            }
          >
            {occupe === "cycle" ? "Réouverture…" : "Rouvrir l'affaire"}
          </button>
        ) : (
          <button
            type="button"
            className="bouton"
            disabled={occupe !== ""}
            onClick={() =>
              void agir("cycle", `/api/v1/affaires/${affaireId}/archive`,
                () => "L'affaire est archivée.")
            }
          >
            {occupe === "cycle" ? "Archivage…" : "Archiver l'affaire"}
          </button>
        )}
      </section>

      {/* 4 — Ce qui ne se défait pas. Encadré, en dernier, à distance. */}
      <section className="reglage reglage--danger">
        <div className="reglage__propos">
          <h3>Supprimer l'affaire</h3>
          <p>
            Détruit les pièces, leurs révisions, l'index et l'espace de travail.{" "}
            <strong>Rien n'est récupérable.</strong> Une affaire d'où un
            chiffrage est sorti, ou dont des révisions sont encore sous
            conservation, est retenue — archivez-la plutôt.
          </p>
        </div>
        <button
          type="button"
          className="bouton bouton--danger"
          onClick={() => poserSuppression(true)}
        >
          Supprimer…
        </button>
      </section>

      {suppression && (
        <Supprimer affaireId={affaireId} fermer={() => poserSuppression(false)} />
      )}
    </div>
  );
}
